import { useEffect, useState } from "react";
import { api, fmtINR, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { DownloadSimple, MagnifyingGlass, FunnelSimple, ClipboardText, CheckCircle, XCircle, CurrencyInr } from "@phosphor-icons/react";

const STAGES = ["department_manager", "hr"];

/**
 * Workflow filter + summary toolbar reused across Travel & Expense pages.
 * kind: "travel" | "expense"
 * onChange: (query object) — parent should refetch using these params
 *
 * Filter visibility scoping (business rule):
 *   super_admin / hr / finance_manager  → all filters incl. employee + department
 *   department_manager (or is_department_manager) → employee filter (own dept only), no dept filter
 *   employee / sales_manager (non-DM)   → NO employee/dept filters (they only see own claims)
 */
export default function WorkflowToolbar({ kind, filters, setFilters, extraTypeOptions = [] }) {
  const { user } = useAuth();
  const role = user?.role;
  const isDM = user?.is_department_manager || role === "department_manager";
  const isElevated = ["super_admin", "hr", "finance_manager"].includes(role);
  const canFilterOthers = isElevated || isDM;
  const canFilterDept = isElevated;

  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const params = {};
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v && k !== "page" && k !== "page_size" && k !== "status") params[k] = v;
    });
    api.get(`/${kind === "travel" ? "travel" : "expenses"}/summary`, { params }).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [kind, filters]);
  useEffect(() => {
    if (!canFilterOthers) return;
    api.get("/employees/lookup/all").then((r) => {
      const list = r.data || [];
      // DM: keep only same-department employees
      if (isDM && !isElevated && user?.department_id) {
        setEmployees(list.filter((e) => e.department_id === user.department_id));
      } else {
        setEmployees(list);
      }
    }).catch(() => setEmployees([]));
    if (canFilterDept) {
      api.get("/departments").then((r) => setDepartments(r.data)).catch(() => setDepartments([]));
    }
  }, [canFilterOthers, canFilterDept, isDM, isElevated, user?.department_id]);

  const setF = (k, v) => setFilters({ ...filters, [k]: v === "__all__" ? "" : v, page: 1 });
  const clear = () => setFilters({ page: 1, page_size: filters.page_size || 25 });

  const download = async () => {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => { if (v && k !== "page" && k !== "page_size") params.set(k, v); });
    const tok = localStorage.getItem("smipl_token");
    const url = `${API_BASE}/${kind === "travel" ? "travel" : "expenses"}/export?${params}&token=${encodeURIComponent(tok || "")}`;
    // Fetch with auth header (backend accepts both) — then trigger download
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${kind}_claims.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error(e);
    }
  };

  const cards = summary ? [
    { label: "Pending", value: summary.pending || 0, icon: ClipboardText, tone: "text-amber-600" },
    { label: "Approved", value: summary.approved || 0, icon: CheckCircle, tone: "text-green-600" },
    { label: "Rejected", value: summary.rejected || 0, icon: XCircle, tone: "text-red-600" },
    { label: "Paid", value: summary.paid || 0, icon: CurrencyInr, tone: "text-purple-600" },
    { label: "Total Claims", value: summary.total_claims || 0, icon: ClipboardText, tone: "text-slate-500" },
    { label: "Total Amount", value: fmtINR(summary.total_amount || 0), icon: CurrencyInr, tone: "text-[#fd7e14]" },
  ] : [];

  return (
    <div className="space-y-3 mb-3">
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid={`${kind}-summary`}>
          {cards.map((c) => (
            <Card key={c.label} className="p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase tracking-wider opacity-60">{c.label}</span>
                <c.icon size={14} className={c.tone} weight="duotone" />
              </div>
              <div className={`text-lg font-semibold ${c.tone}`} style={{ fontFamily: "Outfit, sans-serif" }}>
                {c.value}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 flex-1 min-w-[220px] max-w-md rounded-md px-2.5 py-1.5" style={{ background: "hsl(var(--muted))" }}>
            <MagnifyingGlass size={14} className="opacity-50" />
            <input
              className="bg-transparent outline-none text-sm w-full"
              placeholder="Search claim ID, employee, description…"
              value={filters.q || ""}
              onChange={(e) => setF("q", e.target.value)}
              data-testid={`${kind}-filter-q`}
            />
          </div>
          <Button size="sm" variant="outline" onClick={() => setExpanded((e) => !e)}><FunnelSimple size={14} className="mr-1" /> {expanded ? "Hide" : "Filters"}</Button>
          <Button size="sm" variant="outline" onClick={clear}>Clear</Button>
          <Button size="sm" className="btn-brand ml-auto" onClick={download} data-testid={`${kind}-export`}><DownloadSimple size={14} className="mr-1" /> Export Excel</Button>
        </div>

        {expanded && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            {canFilterOthers && (
              <FilterSelect label="Employee" value={filters.employee_id} onChange={(v) => setF("employee_id", v)} options={employees.map((e) => ({ value: e.id, label: e.full_name }))} testid={`${kind}-filter-employee`} />
            )}
            {canFilterDept && (
              <FilterSelect label="Department" value={filters.department_id} onChange={(v) => setF("department_id", v)} options={departments.map((d) => ({ value: d.id, label: d.name }))} testid={`${kind}-filter-dept`} />
            )}
            <FilterSelect label="Status" value={filters.status} onChange={(v) => setF("status", v)} options={[
              { value: "pending", label: "Pending" }, { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" }, { value: "paid", label: "Paid" },
            ]} testid={`${kind}-filter-status`} />
            <FilterSelect label="Approval Stage" value={filters.current_stage} onChange={(v) => setF("current_stage", v)} options={STAGES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))} testid={`${kind}-filter-stage`} />
            {extraTypeOptions.length > 0 && (
              <FilterSelect label="Type" value={filters.expense_type} onChange={(v) => setF("expense_type", v)} options={extraTypeOptions} testid={`${kind}-filter-type`} />
            )}
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Start date</div>
              <Input type="date" value={filters.start_date || ""} onChange={(e) => setF("start_date", e.target.value)} data-testid={`${kind}-filter-start`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">End date</div>
              <Input type="date" value={filters.end_date || ""} onChange={(e) => setF("end_date", e.target.value)} data-testid={`${kind}-filter-end`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Page size</div>
              <Select value={String(filters.page_size || 25)} onValueChange={(v) => setFilters({ ...filters, page_size: Number(v), page: 1 })}>
                <SelectTrigger data-testid={`${kind}-filter-pagesize`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="25">25</SelectItem><SelectItem value="50">50</SelectItem><SelectItem value="100">100</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">{label}</div>
      <Select value={value || "__all__"} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="All" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
